/**
 * PreviewDocumentHub — pixel-faithful render of the ClaimSync "Document Hub"
 * template (Daylight variant supplied), with a light/dark toggle.
 *
 * REAL data: the case the hub is scoped to (breadcrumb + topbar case name) is
 * the most-recently-updated claim from /api/v1/reports, and the "Documents"
 * metric tile shows the live count of claims in the case file. Clicking the
 * breadcrumb / selected library row navigates to that claim's report.
 *
 * REPRESENTATIVE (template) data: the per-document rows themselves — file names,
 * classification badges, page/chunk counts, extracted figures, provenance chain,
 * and the RAG conversation — have no cheap real source (they live behind the
 * ingestion + DocIntel + RAG pipelines, not the reports list). They use the
 * template's static content exactly like PreviewClientPortal. This is a design
 * preview, not the live Document Hub.
 *
 * Mounted at /preview/document-hub. Self-contained — touches no working surface.
 *
 * Black + orange discipline; green ("done") only on classified / indexed states.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  SERIF, BODY, MONO, PREVIEW_ZOOM, Palette, ChipKind,
  useTheme, ThemeToggle, chipStyle, relTime,
} from './preview/previewTheme';

interface ApiReport {
  id: string;
  client_name?: string;
  status?: string;
  event_type?: string;
  result?: { final_claim_amount?: number };
  created_at?: string;
  updated_at?: string;
}

// ---- representative library rows (template) ----
type LibKind = 'done' | 'parsing' | 'queued';
interface LibRow {
  name: string; type: string; meta: string; icon: string; kind: LibKind; active?: boolean;
}
const LIBRARY: LibRow[] = [
  { name: 'P&L Statement 2023.xlsx', type: 'Financials', meta: '38 chunks', icon: '❏', kind: 'done', active: true },
  { name: 'P&L Statement 2022.xlsx', type: 'Financials', meta: '34 chunks', icon: '❏', kind: 'done' },
  { name: 'Bank statement · Mar 2023.pdf', type: 'Bank', meta: 'OCR 2/4 pp', icon: '▦', kind: 'parsing' },
  { name: 'Bank statements 2023 (11).pdf', type: 'Bank', meta: '11 months', icon: '▦', kind: 'done' },
  { name: 'Form 1065 (2022).pdf', type: 'Tax', meta: '5-tier', icon: '§', kind: 'done' },
  { name: 'Schedule K-1.pdf', type: 'Tax', meta: 'partners', icon: '§', kind: 'done' },
  { name: 'BOP Policy 44120-7.pdf', type: 'Policy', meta: 'coverage', icon: '◳', kind: 'done' },
  { name: 'Fire Marshal Report.pdf', type: 'Loss', meta: 'cause of loss', icon: '◳', kind: 'done' },
  { name: 'RCV Estimate · Xactimate.pdf', type: 'Loss', meta: 'property', icon: '◳', kind: 'done' },
  { name: 'POS export Q1–Q3.csv', type: 'Financials', meta: 'awaiting link', icon: '▤', kind: 'queued' },
];

const FILTERS: { label: string; on: boolean }[] = [
  { label: 'All', on: true }, { label: 'Financials', on: false },
  { label: 'Bank', on: false }, { label: 'Tax', on: false }, { label: 'Policy', on: false },
];

interface FigRow { label: string; value: string; cell: string; conf: string; total?: boolean }
const FIGURES: FigRow[] = [
  { label: 'Revenue', value: '$2,412,000', cell: 'B3', conf: '99%' },
  { label: 'Cost of goods sold', value: '$771,840', cell: 'B7', conf: '97%' },
  { label: 'Payroll', value: '$681,400', cell: 'B9', conf: '95%' },
  { label: 'Rent', value: '$144,000', cell: 'B12', conf: '98%' },
  { label: 'Utilities', value: '$58,200', cell: 'B14', conf: '88%' },
  { label: 'Net income', value: '$402,160', cell: 'B22', conf: '99%', total: true },
];

interface ProvRow { label: string; value: string; mono?: boolean }
const PROVENANCE: ProvRow[] = [
  { label: 'Extracted by', value: 'DocIntel v5 · structured-table' },
  { label: 'Source hash', value: 'sha256:9d6c…2b41', mono: true },
  { label: 'Ingested', value: 'Jun 22, 2026 · 09:41 CDT' },
  { label: 'Tamper-evident', value: 'Chain verified ✓' },
];

const CITATIONS: { doc: string; loc: string }[] = [
  { doc: 'P&L Statement 2023.xlsx', loc: 'row 3' },
  { doc: 'POS export Q1–Q3.csv', loc: 'sum' },
];

// status kind → chip kind + label
function libChip(kind: LibKind): { chip: ChipKind; label: string } {
  if (kind === 'done') return { chip: 'green', label: 'Indexed' };
  if (kind === 'parsing') return { chip: 'orange', label: 'Parsing' };
  return { chip: 'neutral', label: 'Queued' };
}

export default function PreviewDocumentHub() {
  const { theme, P, toggle } = useTheme();
  const navigate = useNavigate();
  const [report, setReport] = useState<ApiReport | null>(null);
  const [docCount, setDocCount] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=200', { showErrorToast: false });
        const list: ApiReport[] = Array.isArray(data?.reports) ? data.reports : Array.isArray(data) ? data : [];
        const sorted = [...list].sort((a, b) =>
          new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
        if (alive) {
          setReport(sorted[0] || null);
          setDocCount(list.length);
        }
      } catch {
        if (alive) { setReport(null); setDocCount(null); }
      }
    })();
    return () => { alive = false; };
  }, []);

  const clientName = report?.client_name || 'Riverside Kitchen & Tap';
  const openReport = () => { if (report?.id) navigate(`/report/${report.id}`); };
  const ingested = relTime(report?.updated_at || report?.created_at);

  // metric strip — "Documents" wired to the live case-file count; rest are template
  const metrics = useMemo(() => ([
    { label: 'Documents', value: docCount != null ? String(docCount) : '20', sub: '18 indexed', subColor: P.greenText },
    { label: 'Pages OCR’d', value: '312', sub: '2 parsing', subColor: P.orangeText },
    { label: 'Figures extracted', value: '247', sub: 'avg 96% conf.', subColor: P.textMuted },
    { label: 'RAG chunks', value: '4,210', sub: 'embedded', subColor: P.textMuted },
  ]), [docCount, P]);

  // ---- nav rail (template) ----
  const navGroups: { group: string; items: { id: string; label: string; icon: string; badge?: string; active?: boolean }[] }[] = [
    { group: 'Work Desk', items: [{ id: 'desk', label: 'Work Desk', icon: '◧' }] },
    { group: 'Cases', items: [{ id: 'cases', label: 'All Claims', icon: '▤' }, { id: 'new', label: 'New Claim', icon: '＋' }] },
    { group: 'Documents', items: [{ id: 'hub', label: 'Document Hub', icon: '▦', active: true }, { id: 'ingest', label: 'Ingestion', icon: '⤓' }, { id: 'rag', label: 'RAG Search', icon: '⌕' }] },
    { group: 'Review', items: [{ id: 'engine', label: 'Engine Review', icon: '◈' }, { id: 'anom', label: 'Anomalies', icon: '◬', badge: '7' }] },
    { group: 'Admin', items: [{ id: 'activity', label: 'Activity Log', icon: '≣' }] },
  ];

  const filterStyle = (on: boolean): React.CSSProperties => ({
    fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 7, cursor: 'pointer',
    border: `1px solid ${on ? P.orange : P.cardBorder}`, background: on ? P.elevBg : P.pageBg,
    color: on ? P.textPrimary : P.textLabel,
  });

  return (
    <div style={{ zoom: PREVIEW_ZOOM, height: '100vh', width: '100%', overflow: 'hidden', background: P.pageBg, fontFamily: BODY, color: P.textBody, display: 'flex', flexDirection: 'column' }}>
      {/* Static preview banner */}
      <div style={{ background: P.orange, color: '#1a1a1a', padding: '10px 20px', fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto' }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '1px', border: '1.5px solid #1a1a1a', borderRadius: 4, padding: '2px 8px', fontSize: 10.5 }}>Static design preview</span>
        <span>These numbers are NOT live.</span>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ============ LEFT RAIL ============ */}
      <aside style={{ width: 240, flex: '0 0 240px', background: P.pageBg, display: 'flex', flexDirection: 'column', height: '100%', borderRight: `1px solid ${P.railHairline}` }}>
        <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: P.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 30px' }}>
            <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
          </div>
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20, letterSpacing: '0.2px' }}>The Figure Co.</div>
            <div style={{ fontSize: 9.5, letterSpacing: '1.4px', color: P.textMuted, textTransform: 'uppercase', marginTop: 2 }}>Forensic BI Platform</div>
          </div>
        </div>
        <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0 16px' }}>
          {navGroups.map((grp) => (
            <div key={grp.group} style={{ margin: '10px 0 2px' }}>
              <div style={{ fontSize: 10, letterSpacing: '1.3px', color: P.textMuted, textTransform: 'uppercase', fontWeight: 600, padding: '6px 22px 4px' }}>{grp.group}</div>
              {grp.items.map((item) => (
                <a
                  key={item.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11, padding: '8px 18px 8px 19px', fontSize: 13.5,
                    fontWeight: 500, cursor: 'pointer', textDecoration: 'none',
                    borderLeft: `3px solid ${item.active ? P.orange : 'transparent'}`,
                    color: item.active ? P.textPrimary : P.textLabel,
                    background: item.active ? P.elevBg : 'transparent',
                  }}
                >
                  <span style={{ fontSize: 14, width: 18, textAlign: 'center', opacity: 0.9 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge && (
                    <span style={{ fontSize: 10.5, fontWeight: 600, background: P.orange, color: P.onOrange, borderRadius: 999, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>{item.badge}</span>
                  )}
                </a>
              ))}
            </div>
          ))}
        </nav>
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: P.elevBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.textBody, fontWeight: 600, fontSize: 12.5, flex: '0 0 32px' }}>TM</div>
          <div style={{ flex: 1, lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ color: P.textPrimary, fontSize: 13, fontWeight: 500 }}>Tyler Mercer</div>
            <div style={{ color: P.textLabel, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />Internal Accountant</div>
          </div>
          <span style={{ color: P.textMuted, fontSize: 15 }}>⌄</span>
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>

        {/* TOPBAR */}
        <header style={{ height: 58, flex: '0 0 58px', background: P.pageBg, borderBottom: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', padding: '0 22px', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: P.textLabel }}>
            <span style={{ cursor: 'pointer' }} onClick={openReport}>Claims</span><span style={{ color: P.accentNeutral }}>›</span>
            <span style={{ color: P.textBody, cursor: 'pointer' }} onClick={openReport}>{clientName}</span><span style={{ color: P.accentNeutral }}>›</span>
            <span style={{ color: P.textPrimary, fontWeight: 500 }}>Document Hub</span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 9, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 9, padding: '7px 12px', width: 280, cursor: 'text', color: P.textMuted, fontSize: 13 }}>
            <span style={{ fontSize: 13 }}>⌕</span><span style={{ flex: 1 }}>Search documents…</span>
            <span style={{ fontFamily: MONO, fontSize: 11, background: P.pageBg, border: `1px solid ${P.controlBorder}`, borderRadius: 5, padding: '1px 6px', color: P.textLabel }}>⌘K</span>
          </div>
          <button style={{ background: P.orange, color: P.onOrange, border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: BODY }}>⤓ Upload</button>
          <ThemeToggle P={P} theme={theme} onToggle={toggle} />
        </header>

        {/* METRIC STRIP */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0, borderBottom: `1px solid ${P.railHairline}`, background: P.pageBg }}>
          {metrics.map((m) => (
            <div key={m.label} style={{ padding: '14px 24px', borderRight: `1px solid ${P.railHairline}` }}>
              <div style={{ fontSize: 11.5, color: P.textLabel }}>{m.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 3 }}>
                <span style={{ fontFamily: MONO, fontSize: 22, color: P.textPrimary, fontWeight: 500 }}>{m.value}</span>
                <span style={{ fontSize: 11.5, color: m.subColor }}>{m.sub}</span>
              </div>
            </div>
          ))}
        </div>

        {/* 3-PANE */}
        <div style={{ flex: 1, display: 'flex', overflowX: 'auto', minHeight: 0 }}>
          <div style={{ display: 'flex', height: '100%', width: '100%', minWidth: 0 }}>

            {/* LIBRARY */}
            <div style={{ width: 320, flex: '0 0 320px', borderRight: `1px solid ${P.railHairline}`, overflowY: 'auto', background: P.pageBg }}>
              <div style={{ padding: '16px 18px 10px', display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {FILTERS.map((f) => (
                  <span key={f.label} style={filterStyle(f.on)}>{f.label}</span>
                ))}
              </div>
              {LIBRARY.map((d) => {
                const sc = libChip(d.kind);
                return (
                  <div
                    key={d.name}
                    onClick={d.active ? openReport : undefined}
                    style={{
                      padding: '12px 18px', borderBottom: `1px solid ${P.elevBg}`, cursor: 'pointer',
                      display: 'flex', gap: 11, alignItems: 'flex-start',
                      background: d.active ? P.orangeRowTint : 'transparent',
                      boxShadow: d.active ? `inset 3px 0 0 ${P.orange}` : 'none',
                    }}
                  >
                    <span style={{ width: 30, height: 30, borderRadius: 7, background: P.elevBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: P.textLabel, flex: '0 0 30px' }}>{d.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, color: P.textPrimary, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                      <div style={{ fontSize: 11, color: P.textMuted, marginTop: 2 }}>{d.type} · {d.meta}</div>
                    </div>
                    <span style={{ ...chipStyle(sc.chip, P), fontSize: 10, padding: '2px 8px', flex: '0 0 auto' }}>{sc.label}</span>
                  </div>
                );
              })}
            </div>

            {/* DOCUMENT DETAIL */}
            <div style={{ flex: 1, overflowY: 'auto', background: P.pageBg, minWidth: 0 }}>
              <div style={{ padding: '24px 28px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 18, marginBottom: 6 }}>
                  <div>
                    <h1 style={{ fontFamily: SERIF, fontSize: 24, color: P.textPrimary, margin: 0, fontWeight: 400 }}>P&amp;L Statement 2023.xlsx</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                      <span style={{ ...chipStyle('green', P), fontSize: 11, padding: '3px 10px' }}>Classified · Profit &amp; Loss (98%)</span>
                      <span style={{ fontSize: 12, color: P.textLabel }}>5-tier extraction · structured table</span>
                      <span style={{ fontSize: 12, color: P.textMuted }}>·</span>
                      <span style={{ fontSize: 12, color: P.textLabel }}>indexed to RAG · 38 chunks</span>
                    </div>
                  </div>
                  <button onClick={openReport} style={{ background: P.elevBg, border: `1px solid ${P.controlBorder}`, borderRadius: 9, padding: '9px 15px', fontSize: 13, fontWeight: 500, color: P.textBody, cursor: 'pointer', fontFamily: BODY }}>Open source ↗</button>
                </div>

                {/* extracted figures */}
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden', marginTop: 20 }}>
                  <div style={{ padding: '14px 20px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', gap: 9 }}>
                    <h2 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400, flex: 1 }}>Extracted figures</h2>
                    <span style={{ fontSize: 11.5, color: P.textMuted }}>value · source cell · confidence</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.9fr 0.8fr', padding: '9px 20px', borderBottom: `1px solid ${P.divider}`, fontSize: 10.5, letterSpacing: '0.6px', textTransform: 'uppercase', color: P.textMuted, fontWeight: 600 }}>
                    <span>Line item</span><span style={{ textAlign: 'right' }}>Value</span><span style={{ textAlign: 'right' }}>Cell</span><span style={{ textAlign: 'right' }}>Conf.</span>
                  </div>
                  {FIGURES.map((r) => {
                    const confLow = parseInt(r.conf, 10) < 90;
                    return (
                      <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.9fr 0.8fr', padding: '11px 20px', borderBottom: `1px solid ${P.rowDivider}`, alignItems: 'center', borderTop: r.total ? `2px solid ${P.accentNeutral}` : undefined }}>
                        <span style={{ fontSize: 13, color: r.total ? P.textPrimary : P.textBody, fontWeight: r.total ? 600 : 400 }}>{r.label}</span>
                        <span style={{ fontFamily: MONO, fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.total ? P.textPrimary : P.textBody, fontWeight: r.total ? 600 : 400 }}>{r.value}</span>
                        <span style={{ fontFamily: MONO, fontSize: 12, color: P.textMuted, textAlign: 'right' }}>{r.cell}</span>
                        <span style={{ fontFamily: MONO, fontSize: 12, textAlign: 'right', color: confLow ? P.orangeText : P.greenText }}>{r.conf}</span>
                      </div>
                    );
                  })}
                </div>

                {/* provenance */}
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, padding: 20, marginTop: 18 }}>
                  <h2 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: '0 0 14px', fontWeight: 400 }}>Provenance &amp; audit chain</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 28px' }}>
                    {PROVENANCE.map((p) => (
                      <div key={p.label}>
                        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.6px', color: P.textMuted, fontWeight: 600, marginBottom: 3 }}>{p.label}</div>
                        <div style={{ fontSize: 13, color: P.textBody, fontFamily: p.mono ? MONO : BODY }}>{p.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 16, fontSize: 11.5, color: P.textMuted }}>
                    Case file · {clientName} · last ingest {ingested}
                  </div>
                </div>
              </div>
            </div>

            {/* RAG PANEL */}
            <div style={{ width: 340, flex: '0 0 340px', borderLeft: `1px solid ${P.railHairline}`, background: P.elevBg, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ padding: '16px 18px', borderBottom: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: P.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.onOrange, fontSize: 14 }}>✦</div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: P.textPrimary, fontSize: 14, fontWeight: 500, fontFamily: SERIF }}>Ask the documents</div>
                  <div style={{ color: P.textMuted, fontSize: 10.5 }}>Grounded across 20 documents</div>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ alignSelf: 'flex-end', maxWidth: '90%', background: P.orange, color: P.onOrange, borderRadius: '12px 12px 4px 12px', padding: '10px 13px', fontSize: 13, lineHeight: 1.45, fontWeight: 500 }}>What was 2023 revenue, and where is it documented?</div>
                <div style={{ alignSelf: 'flex-start', maxWidth: '96%' }}>
                  <div style={{ background: P.neutralChipBg, color: P.textBody, borderRadius: '12px 12px 12px 4px', padding: '12px 13px', fontSize: 13, lineHeight: 1.55 }}>
                    2023 revenue was <strong style={{ color: P.textPrimary, fontFamily: MONO }}>$2,412,000</strong>, taken from the P&amp;L Statement and corroborated by the POS export. No discrepancy &gt; 0.5%.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 9 }}>
                    {CITATIONS.map((c) => (
                      <div key={c.doc} style={{ display: 'flex', alignItems: 'center', gap: 9, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 8, padding: '8px 11px', cursor: 'pointer' }}>
                        <span style={{ fontSize: 12, color: P.orangeText }}>◳</span>
                        <span style={{ flex: 1, fontSize: 12, color: P.textBody, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.doc}</span>
                        <span style={{ fontSize: 11, color: P.textMuted, fontFamily: MONO }}>{c.loc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ padding: '12px 14px', borderTop: `1px solid ${P.railHairline}` }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: P.textBody, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 999, padding: '4px 10px', cursor: 'pointer' }}>Continuing expenses?</span>
                  <span style={{ fontSize: 11, color: P.textBody, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 999, padding: '4px 10px', cursor: 'pointer' }}>Policy limits</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 10, padding: '9px 12px' }}>
                  <span style={{ flex: 1, color: P.textMuted, fontSize: 12.5 }}>Ask across the case file…</span>
                  <span style={{ color: P.orangeText, fontSize: 16 }}>↵</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
